import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../redux/slices/AuthSlice';
import appConfig from '../../config/appConfig';
import {
    startSessionKeepAlive,
    stopSessionKeepAlive,
} from '../../config/httpConfig';

const SESSION_KEY = 'admin_session_active';
const CHANNEL_NAME = 'karncy_admin_auth_channel';
const LAST_ACTIVITY_KEY = 'admin_last_activity';

const IdleTimerModal = ({ children }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    
    // Convert configured minutes to milliseconds (default 15 minutes)
    const IDLE_TIMEOUT_MS = (appConfig.IDLE_TIMEOUT_MINUTES || 15) * 60 * 1000;
    
    const idleTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const channelRef = useRef(null);
    const isLoggingOutRef = useRef(false);

    const handleLogout = useCallback(() => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;
        
        stopSessionKeepAlive();
        clearTimeout(idleTimeoutRef.current);

        try {
            if (channelRef.current) {
                channelRef.current.postMessage({ type: 'LOGOUT' });
            }
        } catch (e) {}

        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SESSION_KEY);
            sessionStorage.clear();
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('user');
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            localStorage.clear();
        }

        try {
            dispatch(logoutUser());
        } catch (error) {}

        window.location.href = '/login';
    }, [dispatch]);

    const resetTimer = useCallback(() => {
        if (!user || isLoggingOutRef.current) return;
        
        lastActivityRef.current = Date.now();
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
            }
        } catch (e) {}

        clearTimeout(idleTimeoutRef.current);
        
        idleTimeoutRef.current = setTimeout(() => {
            handleLogout();
        }, IDLE_TIMEOUT_MS);
    }, [user, IDLE_TIMEOUT_MS, handleLogout]);

    // Setup multi-tab communication channel
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return;

        try {
            const channel = new BroadcastChannel(CHANNEL_NAME);
            channelRef.current = channel;

            channel.onmessage = (event) => {
                const data = event.data;
                if (!data) return;

                if (data.type === 'LOGOUT') {
                    handleLogout();
                } else if (data.type === 'ACTIVITY') {
                    lastActivityRef.current = data.timestamp || Date.now();
                    clearTimeout(idleTimeoutRef.current);
                    if (user && !isLoggingOutRef.current) {
                        idleTimeoutRef.current = setTimeout(() => {
                            handleLogout();
                        }, IDLE_TIMEOUT_MS);
                    }
                } else if (data.type === 'CHECK_ACTIVE_TAB') {
                    if (user && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === 'true') {
                        channel.postMessage({ type: 'ACTIVE_TAB_EXISTS' });
                    }
                }
            };

            return () => {
                channel.close();
                channelRef.current = null;
            };
        } catch (e) {}
    }, [user, IDLE_TIMEOUT_MS, handleLogout]);

    // Check page closure and configure inactivity timer
    useEffect(() => {
        if (!user) {
            stopSessionKeepAlive();
            clearTimeout(idleTimeoutRef.current);
            return;
        }

        // Automatic Logout on Page Closure check
        const hasActiveSession = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === 'true';

        if (!hasActiveSession) {
            if (typeof BroadcastChannel !== 'undefined') {
                try {
                    const probeChannel = new BroadcastChannel(CHANNEL_NAME);
                    let tabResponded = false;

                    probeChannel.onmessage = (event) => {
                        if (event.data?.type === 'ACTIVE_TAB_EXISTS') {
                            tabResponded = true;
                            if (typeof sessionStorage !== 'undefined') {
                                sessionStorage.setItem(SESSION_KEY, 'true');
                            }
                        }
                    };

                    probeChannel.postMessage({ type: 'CHECK_ACTIVE_TAB' });

                    const timer = setTimeout(() => {
                        probeChannel.close();
                        if (!tabResponded && sessionStorage.getItem(SESSION_KEY) !== 'true') {
                            // Page was closed or terminated! Invalidate active session and require login
                            handleLogout();
                        }
                    }, 100);

                    return () => {
                        clearTimeout(timer);
                        probeChannel.close();
                    };
                } catch (e) {
                    handleLogout();
                    return;
                }
            } else {
                handleLogout();
                return;
            }
        }

        startSessionKeepAlive();
        resetTimer();

        const events = ['mousedown', 'mousemove', 'keypress', 'keydown', 'scroll', 'touchstart', 'click'];
        const handleActivity = () => {
            const now = Date.now();
            if (now - lastActivityRef.current > 500) {
                resetTimer();
                try {
                    if (channelRef.current) {
                        channelRef.current.postMessage({ type: 'ACTIVITY', timestamp: now });
                    }
                } catch (e) {}
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now();
                if (now - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
                    handleLogout();
                } else {
                    resetTimer();
                }
            }
        };

        events.forEach((e) => document.addEventListener(e, handleActivity));
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            events.forEach((e) => document.removeEventListener(e, handleActivity));
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearTimeout(idleTimeoutRef.current);
        };
    }, [user, resetTimer, handleLogout, IDLE_TIMEOUT_MS]);

    return children;
};

export default IdleTimerModal;
